import assignExistingUserWebsite from "@carotek/Store/Data/Accounts/Handlers/AssignExistingUser";
import checkIfUserExists from "@carotek/Store/Data/Accounts/Handlers/CheckExsitingUser";
import CarotekColors from "@carotek/utils/Colors";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import {
    getCurrentPage,
    getLocation,
    removeAbsoluteUrl,
} from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SignInChangePasswordForm from "@insite/content-library/Widgets/SignIn/SignInChangePasswordForm";
import SignInResetPasswordForm from "@insite/content-library/Widgets/SignIn/SignInResetPasswordForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    changePasswordInstructions = "changePasswordInstructions",
    fullWidthButton = "fullWidthButton",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.changePasswordInstructions]: string;
        [fields.fullWidthButton]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ returnUrl?: string; externalError?: string }>(location.search);
    const pageType = getCurrentPage(state).type;
    const isSignInPage = pageType === "SignInPage";
    const isConfirmationPage = pageType === "OrderConfirmationPage" || pageType === "OnePageCheckoutConfirmationPage";
    const returnUrl = isSignInPage
        ? removeAbsoluteUrl(parsedQuery.returnUrl)
        : `${location.pathname}${location.search}`;

    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        returnUrl,
        externalError: parsedQuery.externalError,
        isConfirmationPage,
        homePageUrl: getHomePageUrl(state),
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    };
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasHistory;

const mapDispatchToProps = {
    signIn,
    checkIfUserExists,
    assignExistingUserWebsite,
};

export interface SignInExistingAccountStyles {
    form?: InjectableCss;
    rememberMeCheckbox?: CheckboxProps;
    rememberMeCheckboxGroup?: CheckboxGroupComponentProps;
    userNameTextField?: TextFieldProps;
    signInGridContainer?: GridContainerProps;
    icon?: IconProps;
    passwordTextField?: TextFieldProps;
    signInButton?: ButtonPresentationProps;
    signInButtonGridItem?: GridItemProps;
    forgotPasswordGridItem?: GridItemProps;
    rememberMeGridItem?: GridItemProps;
    titleGridItem?: GridItemProps;
    passwordGridItem?: GridItemProps;
    userNameGridItem?: GridItemProps;
    signInExistingAccountTitle?: TypographyProps;
    forgotPasswordLink?: LinkPresentationProps;
    resetPasswordModal?: ModalPresentationProps;
    changePasswordModal?: ModalPresentationProps;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    userExistsGridItem?: GridItemProps;
    linkAccountButton?: ButtonPresentationProps;
    linkAccountButtonWrapper?: InjectableCss;
    userExistsMessage?: TypographyPresentationProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
}

export const signInExistingAccountStyles: SignInExistingAccountStyles = {
    icon: {
        css: css`
            padding-top: 50px;
        `,
    },
    signInExistingAccountTitle: {
        variant: "h3",
        transform: "uppercase",
    },
    userNameGridItem: {
        width: 12,
        css: css`
            padding-bottom: 0;
        `,
    },
    passwordGridItem: {
        width: 12,
        css: css`
            padding-bottom: 0;
        `,
    },
    signInButtonGridItem: {
        width: 12,
        css: css`
            justify-content: center;

            button {
                min-height: 37.8px;
                padding: 0 50px;
                //width: 50%;
                :hover {
                    background-color: ${getColor("danger")};
                    border-color: ${getColor("danger")};
                }
            }
        `,
    },
    rememberMeGridItem: {
        width: 10,
        css: css`
            padding-top: 0;
            padding-bottom: 0;
            [class^="CheckboxStyle"] {
                align-items: flex-start;

                label {
                    font-size: 14px;
                    transform: translateY(-5px);
                }
            }
        `,
    },
    forgotPasswordGridItem: {
        width: 12,
        css: css`
            justify-content: flex-start;
            align-items: center;
            padding-bottom: 0;
        `,
    },
    forgotPasswordLink: {
        typographyProps: {
            size: 14,
        },
    },
    resetPasswordModal: {
        sizeVariant: "small",
    },
    changePasswordModal: {
        sizeVariant: "small",
    },
    form: {
        css: css`
            border: 1px solid ${CarotekColors.grey5};
            background-color: ${CarotekColors.grey6};
            padding: 40px;
            padding-top: 25px;
        `,
    },
    titleGridItem: {
        width: 12,
        css: css`
            padding: 10px;
            margin-top: -9px !important;
            justify-content: center;
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            svg {
                width: 25px;
                height: 25px;
            }
        `,
    },
    userExistsGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            gap: 5px;
        `,
    },
    linkAccountButton: {
        css: css`
            padding: 0px 15px;
            margin-right: 15px;
        `,
    },
    linkAccountButtonWrapper: {
        css: css`
            align-self: flex-end;
        `,
    },
    userExistsMessage: {
        css: css`
            font-size: 15px;
            margin-left: 5px;
        `,
    },
    submitErrorGridItem: {
        width: 12,
    },
    submitErrorTitle: {
        color: "danger",
    },
};

/**
 * @deprecated Use signInExistingAccountStyles instead.
 */
export const signInExistingAccount = signInExistingAccountStyles;
const styles = signInExistingAccountStyles;
const StyledForm = getStyledWrapper("form");

const SignInExistingAccount = ({
    signIn,
    checkIfUserExists,
    assignExistingUserWebsite,
    fields,
    accountSettings,
    returnUrl,
    externalError,
    isConfirmationPage,
    homePageUrl,
    theme,
    history,
    signInPageUrl,
}: Props) => {
    const [rememberMe, setRememberMe] = useState(false);
    const [keepMeSignedIn, setKeepMeSignedIn] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [isSignInClicked, setIsSignInClicked] = useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(externalError || "");

    const [isExistingUser, setIsExistingUser] = useState(false);
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const [assignWebsiteSubmitted, setAssignWebsiteSubmitted] = useState(false);
    const [existingEmailError, setExistingEmailError] = useState("");
    const toasterContext = useContext(ToasterContext);

    const showRememberMe = accountSettings.rememberMe;
    const showKeepMeSignedIn = accountSettings.keepMeSignedIn;

    const currentDateTime = new Date().toLocaleString();

    const handleExistingUser = (data: any, error: any) => {
        if (data.apiResult?.isSuccess) {
            setIsCheckingEmail(false);
            if (data.apiResult?.isDataFound) {
                if (data.apiResult?.assignedToCurrentWebsite) {
                    setErrorMessage(error);
                } else {
                    setIsExistingUser(true);
                }
            } else {
                setErrorMessage(error);
            }
        } else {
            setIsCheckingEmail(false);
            setExistingEmailError(data.apiResult?.message);
        }
    };

    const handleWebsiteAssign = () => {
        setAssignWebsiteSubmitted(true);
        if (userName) {
            assignExistingUserWebsite({
                email: userName,
                onSuccess: handleWebsiteAssignSuccessfull,
                onError: () => {
                    setExistingEmailError("Something went wrong. Please try again later.");
                    setAssignWebsiteSubmitted(false);
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult?.successful) {
                        // "this" is targeting the object being created, not the parent SFC

                        this.onSuccess?.(resultProps.apiResult.result);
                    } else if (resultProps.apiResult?.errorMessage) {
                        // "this" is targeting the object being created, not the parent SFC

                        this.onError?.(resultProps.apiResult.errorMessage);
                    }
                },
            });
        }
    };

    const handleWebsiteAssignSuccessfull = () => {
        toasterContext.addToast({ body: translate("Account Successfully Linked"), messageType: "success" });
        window.location.reload();
    };

    const signInHandler = (newPassword?: string) => {
        const pass = newPassword || password;
        setIsSignInClicked(true);
        if (!userName || !pass) {
            return;
        }

        if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
            return;
        }

        setErrorMessage("");

        signIn({
            userName,
            password: pass,
            rememberMe,
            keepMeSignedIn,
            returnUrl: theme.signIn?.useOverlay && isConfirmationPage ? homePageUrl : returnUrl,
            skipRedirect: theme.signIn?.useOverlay && !isConfirmationPage,
            onError: (error, statusCode) => {
                if (statusCode === 422) {
                    setIsChangePasswordModalOpen(true);
                    return;
                }

                setIsExistingUser(false);
                setExistingEmailError("");
                setIsCheckingEmail(true);
                if (userName) {
                    checkIfUserExists({
                        email: userName,
                        currentDateTime,
                        onComplete(result) {
                            handleExistingUser(result, error);
                        },
                    });
                }
            },
        });
    };

    const submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        signInHandler();
    };

    const onResetPasswordClose = () => {
        setIsResetPasswordModalOpen(false);
    };

    const onChangePasswordClose = () => {
        setIsChangePasswordModalOpen(false);
    };

    const onPasswordChanged = (newPassword: string) => {
        setPassword(newPassword);
        setIsChangePasswordModalOpen(false);
        signInHandler(newPassword);
    };

    const validateUserName = () => {
        if (isSignInClicked) {
            if (userName) {
                if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
                    return siteMessage("SignInInfo_Email_Validation");
                }
            } else {
                return accountSettings.useEmailAsUserName
                    ? siteMessage("SignInInfo_Email_Required")
                    : siteMessage("SignInInfo_UserName_Required");
            }
        }

        return "";
    };

    const userErrorMessage = validateUserName();
    const passwordErrorMessage =
        !password && isSignInClicked ? siteMessage("SignInInfo_Password_Required") : errorMessage || "";

    const signInButtonCss = css`
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    width: 100%;
                `,
                css`
                    width: 100%;
                `,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
            ])}
    `;

    return (
        <>
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.signInExistingAccountTitle}>{translate("Sign In")}</Typography>
            </GridItem>
            <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
                <GridContainer {...styles.signInGridContainer}>
                    <GridItem {...styles.userNameGridItem}>
                        <TextField
                            id="userName"
                            {...styles.userNameTextField}
                            label={accountSettings.useEmailAsUserName ? translate("Email") : translate("User Name")}
                            onChange={e => setUserName(e.currentTarget.value)}
                            value={userName}
                            error={userErrorMessage}
                            autoComplete="username"
                            data-test-selector="signIn_userName"
                        />
                    </GridItem>
                    <GridItem {...styles.passwordGridItem}>
                        <TextField
                            id="password"
                            {...styles.passwordTextField}
                            label={translate("Password")}
                            onChange={e => setPassword(e.currentTarget.value)}
                            value={password}
                            error={passwordErrorMessage}
                            iconProps={{
                                ...styles.icon,
                                src: showPassword ? EyeOff : Eye,
                            }}
                            iconClickableProps={{
                                onClick: () => {
                                    setShowPassword(!showPassword);
                                },
                                type: "button",
                            }}
                            clickableText={password}
                            autoComplete="current-password"
                            data-test-selector="signIn_password"
                            type={showPassword ? "text" : "password"}
                        />
                    </GridItem>
                    {isCheckingEmail && (
                        <GridItem width={12}>
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        </GridItem>
                    )}

                    {isExistingUser && (
                        <GridItem {...styles.userExistsGridItem}>
                            <Typography {...styles.userExistsMessage}>
                                An account with this email already exists in Sunsource. Would you like to connect that
                                account to Carotek?
                            </Typography>
                            <Typography {...styles.userExistsMessage}>
                                If you do not want to connect the accounts you will have to use a different email
                                address to register on Carotek.
                            </Typography>
                            <StyledWrapper {...styles.linkAccountButtonWrapper}>
                                <Button
                                    {...styles.linkAccountButton}
                                    onClick={() => handleWebsiteAssign()}
                                    disabled={assignWebsiteSubmitted}
                                >
                                    Link Account
                                </Button>
                                <Button
                                    {...styles.linkAccountButton}
                                    onClick={() => setIsExistingUser(false)}
                                    disabled={assignWebsiteSubmitted}
                                >
                                    Cancel
                                </Button>
                            </StyledWrapper>
                        </GridItem>
                    )}

                    {existingEmailError && (
                        <GridItem data-test-selector="createAccount_ErrorMessage" {...styles.submitErrorGridItem}>
                            <Typography {...styles.submitErrorTitle}>{existingEmailError}</Typography>
                        </GridItem>
                    )}

                    {!isExistingUser && (
                        <>
                            <GridItem {...styles.forgotPasswordGridItem}>
                                <Link
                                    {...styles.forgotPasswordLink}
                                    type="button"
                                    onClick={() => setIsResetPasswordModalOpen(true)}
                                    data-test-selector="signIn_forgotPassword"
                                >
                                    {translate("Forgot Password?")}
                                </Link>
                                <Modal
                                    headline={translate("Reset Password")}
                                    {...styles.resetPasswordModal}
                                    isOpen={isResetPasswordModalOpen}
                                    handleClose={() => onResetPasswordClose()}
                                    data-test-selector="resetPasswordModal"
                                >
                                    <SignInResetPasswordForm onClose={() => onResetPasswordClose()} />
                                </Modal>
                            </GridItem>
                            <GridItem {...styles.rememberMeGridItem}>
                                {showRememberMe && (
                                    <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                                        <Checkbox
                                            uid="rememberMe"
                                            {...styles.rememberMeCheckbox}
                                            checked={rememberMe}
                                            onChange={(_, value) => setRememberMe(value)}
                                        >
                                            {translate("Remember Me - Carotek")}
                                        </Checkbox>
                                    </CheckboxGroup>
                                )}
                                {showKeepMeSignedIn && (
                                    <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                                        <Checkbox
                                            uid="keepMeSignedIn"
                                            {...styles.rememberMeCheckbox}
                                            checked={keepMeSignedIn}
                                            onChange={(_, value) => setKeepMeSignedIn(value)}
                                        >
                                            {translate("Keep Me Signed In")}
                                        </Checkbox>
                                    </CheckboxGroup>
                                )}
                            </GridItem>

                            <GridItem {...styles.signInButtonGridItem}>
                                <Button
                                    {...styles.signInButton}
                                    css={signInButtonCss}
                                    type="submit"
                                    data-test-selector="signIn_submit"
                                >
                                    {translate("Sign In - Carotek")}
                                </Button>
                                <Modal
                                    headline={translate("Change Password")}
                                    data-test-selector="changePasswordModal"
                                    {...styles.changePasswordModal}
                                    isOpen={isChangePasswordModalOpen}
                                    handleClose={onChangePasswordClose}
                                >
                                    <SignInChangePasswordForm
                                        enteredUserName={userName}
                                        enteredPassword={password}
                                        instructionsText={fields.changePasswordInstructions}
                                        onPasswordChanged={onPasswordChanged}
                                    />
                                </Modal>
                            </GridItem>
                        </>
                    )}
                </GridContainer>
            </StyledForm>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(SignInExistingAccount)),
    definition: {
        allowedContexts: ["SignInPage", "SignInOverlay"],
        group: "Sign In",
        icon: "LogIn",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.changePasswordInstructions,
                editorTemplate: "RichTextField",
                displayName: "Change Password Instructions",
                defaultValue: "A password change is required for your account before you may continue.",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                sortOrder: 0,
            },
            {
                name: fields.fullWidthButton,
                displayName: "Full Width Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
